import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { authCookies, getLangId } from './cookies';
import { buildAuthHeader } from './sign';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(handler: UnauthorizedHandler) {
  onUnauthorized = handler;
}

function normalizeApiPath(url: string): string {
  if (url.startsWith(API_BASE)) return url.slice(API_BASE.length) || '/';
  return url.startsWith('/') ? url : `/${url}`;
}

// Pre-auth ручки: работают без авторизации (бек пропускает аноним —
// QueryParamAuth.php:37). Их НЕЛЬЗЯ подписывать токеном из куки: после смены
// пароля (или восстановления фикстур) auth_key в куке протухает, и серверный
// auth-фильтр отвечает 401 ещё ДО экшена. Тогда
// /user/authToken (impersonation) и /user/login не могут переавторизоваться,
// пока куки не почистишь вручную. Поэтому для этих путей подпись не вешаем.
const PRE_AUTH_PATHS = [
  '/user/login',
  '/user/check2fa',
  '/user/authToken',
  '/user/register',
  '/user/requestResetPassword',
  '/user/resetPassword',
  '/user/verify',
];

function isPreAuthPath(path: string): boolean {
  return PRE_AUTH_PATHS.some(
    (base) => path === base || path.startsWith(`${base}/`),
  );
}

// Нормализуем axios `params` (объект, undefined, или URLSearchParams) к
// плоской паре key→string для включения в подпись запроса.
function axiosParamsToObject(
  raw: unknown,
): Record<string, string | number | boolean | undefined> {
  if (!raw) return {};
  if (raw instanceof URLSearchParams) {
    const out: Record<string, string> = {};
    raw.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  if (typeof raw === 'object') {
    return raw as Record<string, string | number | boolean | undefined>;
  }
  return {};
}

function signInterceptor(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const langId = getLangId();
  const url = config.url ?? '';
  const path = normalizeApiPath(url);
  const authKey = authCookies.getAuthKey();
  const userId = authCookies.getUserId();

  // Параметры могут приехать тремя путями: query-string в `config.url`,
  // объект в `config.params`, и lang-id который мы сами добавим.
  // Все три должны попасть в подпись в правильном порядке — иначе сервер
  // вернёт 401 (Invalid signature).
  const axiosParams = axiosParamsToObject(config.params);

  if (authKey && userId !== null && !isPreAuthPath(path)) {
    const timestamp = Math.round(Date.now() / 1000);
    const { header } = buildAuthHeader({
      url: path,
      authKey,
      serviceId: userId,
      timestamp,
      extraParams: { ...axiosParams, 'lang-id': langId },
    });
    config.headers.set('Authorization', header);
  }
  config.params = { ...axiosParams, 'lang-id': langId };

  return config;
}

// Единое правило: любой HTTP 401 от API = сессия невалидна → принудительный
// разлогин (onUnauthorized: clear cookies + setGuest → RequireAuth редиректит
// на /login + тост). 401 приходит только с защищённых ручек (pre-auth/login
// не подписываются и не отдают 401), поэтому ложных срабатываний нет.
function responseErrorInterceptor(error: AxiosError) {
  if (error.response?.status === 401) {
    onUnauthorized();
  }
  return Promise.reject(error);
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
});

apiClient.interceptors.request.use(signInterceptor);
// Невалидная подпись теперь приходит честным HTTP 401 (бек: QueryParamAuth →
// UnauthorizedHttpException, known-server-issues #4) и обрабатывается общим
// error-интерсептором. Прежний шим detectWrongSignature (ловил plain-text
// "Wrong signature" с HTTP 200) удалён за ненадобностью.
apiClient.interceptors.response.use(undefined, responseErrorInterceptor);
